import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Search, Pencil, Trash2, MoreVertical, Package, Tag } from 'lucide-react';
import { useProductos } from '@/hooks/useData';
import type { ProductoServicio, UnidadProducto } from '@/lib/types';
import { formatEUR } from '@/lib/calc';
import { AppLayout } from '@/components/AppLayout';

const UNIDADES: UnidadProducto[] = ['ud', 'h', 'kg', 'm', 'm²', 'm³', 'l', 'servicio', 'otro'];

const PROD_VACIO: Omit<ProductoServicio, 'id'> = {
  codigo: '', nombre: '', descripcion: '', precioBase: 0,
  iva: 21, descuento: 0, unidad: 'ud', categoria: '', activo: true,
};

export default function ProductosServicios() {
  const { productos, crear, actualizar, eliminar } = useProductos();
  const [busqueda, setBusqueda] = useState('');
  const [filtroCat, setFiltroCat] = useState('Todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProductoServicio | null>(null);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<ProductoServicio, 'id'>>(PROD_VACIO);

  const categorias = ['Todos', ...Array.from(new Set(productos.map(p => p.categoria).filter(Boolean)))];

  const filtrados = productos.filter(p => {
    const q = busqueda.toLowerCase();
    const matchQ = !busqueda || p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q) || p.descripcion.toLowerCase().includes(q) || p.categoria.toLowerCase().includes(q);
    const matchCat = filtroCat === 'Todos' || p.categoria === filtroCat;
    return matchQ && matchCat;
  });

  const openNew = () => { setEditTarget(null); setForm(PROD_VACIO); setModalOpen(true); };
  const openEdit = (p: ProductoServicio) => { setEditTarget(p); setForm({ codigo: p.codigo, nombre: p.nombre, descripcion: p.descripcion, precioBase: p.precioBase, iva: p.iva, descuento: p.descuento, unidad: p.unidad, categoria: p.categoria, activo: p.activo }); setModalOpen(true); };

  const handleSave = () => {
    if (!form.nombre.trim()) return;
    if (editTarget) actualizar({ ...editTarget, ...form });
    else crear(form);
    setModalOpen(false);
  };

  const toggleActivo = (p: ProductoServicio) => actualizar({ ...p, activo: !p.activo });

  return (
    <AppLayout title="Productos y Servicios" active="productos">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* Buscador + filtro categoría + botón */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Buscar por nombre, código o categoría…" value={busqueda} onChange={e => setBusqueda(e.target.value)} className="pl-9" />
          </div>
          <Select value={filtroCat} onValueChange={setFiltroCat}>
            <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} className="gap-1.5 shrink-0"><Plus size={16} /> Nuevo producto</Button>
        </div>

        {/* Contadores */}
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5"><Package size={14} /> <strong>{productos.length}</strong> productos/servicios</span>
          <span className="flex items-center gap-1.5"><Tag size={14} /> <strong>{categorias.length - 1}</strong> categorías</span>
          <span className="text-emerald-600"><strong>{productos.filter(p => p.activo).length}</strong> activos</span>
          {productos.filter(p => !p.activo).length > 0 && (
            <span className="text-muted-foreground"><strong>{productos.filter(p => !p.activo).length}</strong> inactivos</span>
          )}
        </div>

        {/* Lista */}
        {filtrados.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mb-3"><Package size={24} className="text-muted-foreground" /></div>
            <p className="font-semibold mb-1">{busqueda || filtroCat !== 'Todos' ? 'Sin resultados' : 'Aún no hay productos'}</p>
            <p className="text-sm text-muted-foreground mb-4">{busqueda || filtroCat !== 'Todos' ? 'Prueba con otros filtros' : 'Añade los productos y servicios que usas habitualmente'}</p>
            {!busqueda && filtroCat === 'Todos' && <Button onClick={openNew} className="gap-1.5"><Plus size={16} /> Nuevo producto</Button>}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[80px_1fr_120px_90px_70px_70px_70px_44px] gap-2 px-4 py-2 border-b border-border bg-muted/40">
              {['Código', 'Nombre / Descripción', 'Categoría', 'Precio', 'IVA%', 'Dto%', 'Estado', ''].map(h => (
                <span key={h} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{h}</span>
              ))}
            </div>
            <AnimatePresence>
              {filtrados.map((p, i) => (
                <motion.div
                  key={p.id} layout
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className={`grid grid-cols-1 md:grid-cols-[80px_1fr_120px_90px_70px_70px_70px_44px] gap-2 items-center px-4 py-3 hover:bg-muted/30 transition-colors ${!p.activo ? 'opacity-55' : ''} ${i < filtrados.length - 1 ? 'border-b border-border' : ''}`}
                >
                  <span className="font-mono text-xs text-muted-foreground">{p.codigo || '—'}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{p.nombre}</p>
                    {p.descripcion && <p className="text-xs text-muted-foreground truncate">{p.descripcion}</p>}
                    <span className="text-xs text-muted-foreground md:hidden">{p.categoria}</span>
                  </div>
                  <span className="hidden md:block">
                    {p.categoria && <Badge variant="outline" className="text-xs font-normal">{p.categoria}</Badge>}
                  </span>
                  <span className="font-mono font-semibold text-sm">{formatEUR(p.precioBase)}<span className="text-xs font-normal text-muted-foreground ml-1">/{p.unidad}</span></span>
                  <span className="font-mono text-xs text-center">{p.iva}%</span>
                  <span className="font-mono text-xs text-center text-emerald-600">{p.descuento > 0 ? `-${p.descuento}%` : '—'}</span>
                  <div className="flex items-center">
                    <Switch checked={p.activo} onCheckedChange={() => toggleActivo(p)} className="scale-75" />
                  </div>
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical size={15} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={() => openEdit(p)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActivo(p)}>{p.activo ? '⏸ Desactivar' : '▶ Activar'}</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setToDelete(p.id)} className="text-destructive focus:text-destructive"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Modal editar / crear */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Editar producto/servicio' : 'Nuevo producto/servicio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <F label="Código">
                <Input value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))} className="font-mono" placeholder="MOB-001" />
              </F>
              <F label="Categoría">
                <Input value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} placeholder="Mano de obra, Cristales…" list="cats-list" />
                <datalist id="cats-list">
                  {Array.from(new Set(productos.map(p => p.categoria).filter(Boolean))).map(c => <option key={c} value={c} />)}
                </datalist>
              </F>
            </div>
            <F label="Nombre *">
              <Input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre del producto o servicio" />
            </F>
            <F label="Descripción">
              <Textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} rows={2} placeholder="Descripción detallada…" />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Precio base (€)">
                <Input type="number" min="0" step="0.01" value={form.precioBase} onChange={e => setForm(f => ({ ...f, precioBase: parseFloat(e.target.value) || 0 }))} className="font-mono" />
              </F>
              <F label="Unidad">
                <Select value={form.unidad} onValueChange={v => setForm(f => ({ ...f, unidad: v as UnidadProducto }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIDADES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="IVA (%)">
                <Select value={String(form.iva)} onValueChange={v => setForm(f => ({ ...f, iva: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="4">4%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="21">21%</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Descuento estándar (%)">
                <Input type="number" min="0" max="100" step="1" value={form.descuento} onChange={e => setForm(f => ({ ...f, descuento: parseFloat(e.target.value) || 0 }))} className="font-mono" />
              </F>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.activo} onCheckedChange={v => setForm(f => ({ ...f, activo: v }))} />
              <Label className="text-sm">Producto activo (visible en catálogo)</Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={!form.nombre.trim()}>{editTarget ? 'Guardar cambios' : 'Crear producto'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (toDelete) eliminar(toDelete); setToDelete(null); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
